import React, { useEffect, useState } from 'react';
import { Box, Typography, CircularProgress, Stack } from '@mui/material';
import axios from 'axios';

/** Codigos WMO (https://open-meteo.com/en/docs) agrupados a un icono/etiqueta simple. */
function weatherCodeInfo(code) {
  if (code === 0) return { icon: '☀️', label: 'Despejado' };
  if (code === 1 || code === 2) return { icon: '⛅', label: 'Parcialmente nublado' };
  if (code === 3) return { icon: '☁️', label: 'Nublado' };
  if (code === 45 || code === 48) return { icon: '🌫️', label: 'Niebla' };
  if (code >= 51 && code <= 57) return { icon: '🌦️', label: 'Llovizna' };
  if (code >= 61 && code <= 67) return { icon: '🌧️', label: 'Lluvia' };
  if (code >= 71 && code <= 77) return { icon: '🌨️', label: 'Nieve' };
  if (code >= 80 && code <= 82) return { icon: '🌧️', label: 'Chubascos' };
  if (code >= 95) return { icon: '⛈️', label: 'Tormenta' };
  return { icon: '🌡️', label: '' };
}

const DAY_NAMES_SHORT = ['DOM', 'LUN', 'MAR', 'MIÉ', 'JUE', 'VIE', 'SÁB'];

function formatDayLabel(dateStr, index) {
  if (index === 0) return 'HOY';
  const d = new Date(`${dateStr}T00:00:00`);
  if (Number.isNaN(d.getTime())) return dateStr;
  return DAY_NAMES_SHORT[d.getDay()];
}

/** Filas listas para exportar a Excel: una por día de pronóstico. */
export function weatherRowsForExport(data) {
  const days = data?.daily?.time || [];
  return days.map((date, i) => ({
    Fecha: date,
    Condición: weatherCodeInfo(data.daily.weathercode?.[i]).label,
    'Temp. máxima (°C)': data.daily.temperature_2m_max?.[i] ?? null,
    'Temp. mínima (°C)': data.daily.temperature_2m_min?.[i] ?? null,
  }));
}

/**
 * Widget de clima para el dashboard: consulta /api/weather (proxy a Open-Meteo)
 * para una coordenada fija y muestra el pronostico de los proximos dias.
 */
const WeatherWidget = ({ lat, lon, getAuthHeaders, onDataLoaded }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (lat == null || lon == null) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError('');
    axios
      .get('/api/weather', { params: { lat, lon }, headers: getAuthHeaders?.() })
      .then((res) => {
        if (cancelled) return;
        if (res.data?.success) {
          setData(res.data.data);
          onDataLoaded?.(res.data.data);
        } else {
          setError(res.data?.error || 'No se pudo obtener el clima');
        }
      })
      .catch((err) => {
        if (!cancelled) setError(err.response?.data?.error || err.message || 'No se pudo obtener el clima');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lat, lon, getAuthHeaders]);

  if (lat == null || lon == null) {
    return (
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">Sin coordenadas configuradas</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', p: 1 }}>
      {loading && (
        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <CircularProgress size={28} />
        </Box>
      )}

      {!loading && error && (
        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Typography variant="body2" color="error">{error}</Typography>
        </Box>
      )}

      {!loading && !error && data && (
        <Stack direction="row" divider={<Box sx={{ borderLeft: '1px solid', borderColor: 'divider' }} />} sx={{ flex: 1, overflowX: 'auto', alignItems: 'stretch', justifyContent: 'space-evenly' }}>
          {(data.daily?.time || []).map((date, i) => {
            const info = weatherCodeInfo(data.daily.weathercode?.[i]);
            const max = data.daily.temperature_2m_max?.[i];
            const min = data.daily.temperature_2m_min?.[i];
            return (
              <Box key={date} sx={{ textAlign: 'center', minWidth: 76, flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', py: 0.5 }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 'bold' }}>
                  {formatDayLabel(date, i)}
                </Typography>
                <Typography sx={{ fontSize: '2.5rem', lineHeight: 1.3 }} title={info.label}>
                  {info.icon}
                </Typography>
                <Typography variant="body1">
                  {Number.isFinite(max) ? Math.round(max) : '--'}° / {Number.isFinite(min) ? Math.round(min) : '--'}°
                </Typography>
              </Box>
            );
          })}
        </Stack>
      )}
    </Box>
  );
};

export default WeatherWidget;
